import React from 'react';
import ProgressBar, { StickyTasklistDocBar } from '@/components/ProgressBar/ProgressBar';

export type TaskStatusVariant = 'default' | 'tasklistStickyFooter';

export interface TaskStatusProps {
  heading?: string;
  /** When false, the readiness heading above the progress bar is omitted. */
  showHeading?: boolean;
  /** When true (and the task is not yet marked complete), progress + action sit on one row. */
  inlineToolbar?: boolean;
  /** Sticky checklist footer: doc bottom bar (progress + CTA / completion pill). */
  variant?: TaskStatusVariant;
  /**
   * When `variant` is `tasklistStickyFooter` and the page has no subtask list (e.g. “Mark Read” only),
   * set true to hide the progress bar and show only the primary CTA.
   */
  omitTasklistFooterProgress?: boolean;
  /** With `tasklistStickyFooter` while subtasks remain: primary CTA (e.g. scroll to “What To Do Next”). */
  onJumpToNextSection?: () => void;
  jumpToNextSectionLabel?: string;
  progressLabelLeft?: string;
  markCompleteLabel?: string;
  completedCount: number;
  /** Total subtasks */
  totalCount: number;
  onMarkComplete?: () => void;
  allowMarkIncomplete?: boolean;
  markCompletePending?: boolean;
  idPrefix?: string;
  taskFinished?: boolean;
  onMarkIncomplete?: () => void;
  completedHeading?: string;
  markIncompleteLabel?: string;
}

const TaskStatus: React.FC<TaskStatusProps> = ({
  heading = 'Ready to mark this task complete?',
  showHeading = true,
  inlineToolbar = false,
  variant = 'default',
  omitTasklistFooterProgress = false,
  onJumpToNextSection,
  jumpToNextSectionLabel = 'What To Do Next',
  progressLabelLeft = 'Progress',
  markCompleteLabel,
  completedCount,
  totalCount,
  onMarkComplete,
  allowMarkIncomplete = false,
  markCompletePending = false,
  idPrefix = 'task-status',
  taskFinished = false,
  onMarkIncomplete,
  completedHeading = 'Task complete!',
  markIncompleteLabel = 'Mark as incomplete',
}) => {
  const pct = totalCount > 0 ? (completedCount / totalCount) * 100 : 0;
  const allDone = totalCount > 0 && completedCount === totalCount;
  const markDisabled =
    markCompletePending || (!allowMarkIncomplete && !allDone) || totalCount === 0;

  const progressId = `${idPrefix}-progress`;
  const progressLabelId = `${idPrefix}-progress-label`;
  const showSuccess = taskFinished && allDone;
  const isTasklistFooter = variant === 'tasklistStickyFooter';

  if (isTasklistFooter) {
    return (
      <StickyTasklistDocBar
        idPrefix={idPrefix}
        completedCount={completedCount}
        totalCount={totalCount}
        taskFinished={taskFinished}
        omitProgress={omitTasklistFooterProgress}
        isAction={!omitTasklistFooterProgress}
        allChecked={allDone}
        onMarkComplete={onMarkComplete}
        onMarkIncomplete={onMarkIncomplete}
        onJumpToNext={onJumpToNextSection}
        jumpToNextLabel={jumpToNextSectionLabel}
        markCompleteLabel={markCompleteLabel}
        completedPillLabel={completedHeading}
        markIncompleteLabel={markIncompleteLabel}
        markCompletePending={markCompletePending}
        allowMarkIncomplete={allowMarkIncomplete}
        progressLabelLeft={progressLabelLeft}
      />
    );
  }

  const completedSummary =
    totalCount === 0 ? '—' : `${completedCount} of ${totalCount} completed`;

  const progressBlockStandard = (
    <div className="task-status-panel__progress">
      <div className="task-status-panel__progress-labels">
        <span className="task-status-panel__progress-left">{progressLabelLeft}</span>
        <span className="task-status-panel__progress-right">{completedSummary}</span>
      </div>
      <ProgressBar id={progressId} value={pct} />
    </div>
  );

  const markCompleteButton = (
    <div className="task-status-panel__actions">
      <button
        type="button"
        className="task-status-panel__btn btn"
        disabled={markDisabled}
        onClick={onMarkComplete}
      >
        {markCompleteLabel ?? 'Mark Task Complete'}
      </button>
    </div>
  );

  const headingBlock =
    showHeading && heading ? (
      <h2 id={`${idPrefix}-heading`} className="task-status-panel__heading">
        {heading}
      </h2>
    ) : null;

  const incompleteBodyDefault =
    inlineToolbar && !showSuccess ? (
      <>
        {headingBlock}
        <div className="task-status-panel__inline-toolbar">
          {progressBlockStandard}
          {markCompleteButton}
        </div>
      </>
    ) : (
      <>
        {!showSuccess ? headingBlock : null}
        {progressBlockStandard}
        {showSuccess ? null : markCompleteButton}
      </>
    );

  const panelClassName = ['task-status-panel', showSuccess ? 'task-status-panel--complete' : '']
    .filter(Boolean)
    .join(' ');

  const ariaLabelledBy = showSuccess
    ? `${idPrefix}-status-msg`
    : showHeading && heading
      ? `${idPrefix}-heading`
      : progressId;

  return (
    <section className={panelClassName} aria-labelledby={ariaLabelledBy}>
      {showSuccess ? (
        <>
          {progressBlockStandard}
          <div className="task-status-panel__finished" id={`${idPrefix}-finished`}>
            <div
              id={`${idPrefix}-status-msg`}
              className="task-status-panel__success-row"
              role="status"
              aria-live="polite"
            >
              <span className="task-status-panel__success-icon" aria-hidden="true">
                <svg width="14" height="11" viewBox="0 0 14 11" fill="none">
                  <path
                    d="M12.25 1.625L5.25 8.625L1.75 5.125"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </span>
              <span className="task-status-panel__success-title">{completedHeading}</span>
            </div>
            {onMarkIncomplete ? (
              <button
                type="button"
                className="task-status-panel__link-incomplete btn btn-link p-0"
                onClick={onMarkIncomplete}
              >
                {markIncompleteLabel}
              </button>
            ) : null}
          </div>
        </>
      ) : (
        incompleteBodyDefault
      )}
    </section>
  );
};

export default TaskStatus;
